<template>
  <div class="cf-section">
    <div class="flex items-center justify-between mb-2">
      <h6 class="font-bold mb-0">Campos personalizados</h6>
      <router-link :to="{ name: 'custom-field-list' }" class="text-sm">Administrar campos</router-link>
    </div>

    <p v-if="!loading && items.length === 0" class="text-muted text-sm">
      No hay campos personalizados para {{ entityLabel }}. RRHH puede crearlos en Mantenimiento &gt; Campos personalizados.
    </p>

    <div v-for="group in groups" :key="group.key" class="mb-3">
      <div v-if="groups.length > 1 || group.key !== 'ALL'" class="text-xs text-muted mb-1">{{ group.label }}</div>
      <div class="flex flex-wrap -mx-2">
        <div v-for="it in group.items" :key="it.field.id" class="w-full md:w-1/2 lg:w-1/3 mb-3 px-2">
          <label class="font-medium">
            {{ it.field.label }} <span v-if="it.field.isRequired" class="text-danger">*</span>
          </label>

          <wz-select v-if="it.field.fieldType === 'LISTA'" v-model="values[it.field.id]"
            :items="it.field.options.map(o => ({ key: o, label: o }))" defaultOption="Seleccionar" />
          <wz-select v-else-if="it.field.fieldType === 'BOOLEANO'" v-model="values[it.field.id]"
            :items="[{ key: 'true', label: 'Sí' }, { key: 'false', label: 'No' }]" defaultOption="Seleccionar" />
          <wz-datepicker v-else-if="it.field.fieldType === 'FECHA'" v-model="values[it.field.id]" />
          <wz-input v-else-if="it.field.fieldType === 'NUMERO' || it.field.fieldType === 'MONTO'"
            type="number" step="any" v-model="values[it.field.id]" />
          <wz-input v-else v-model="values[it.field.id]" maxlength="1000" />

          <small class="text-muted">{{ it.field.placeholder }}</small>
        </div>
      </div>
    </div>

    <div v-if="items.length > 0" class="flex justify-end">
      <wz-button type="button" variant="primary" :disabled="saving" @click="save">Guardar campos personalizados</wz-button>
    </div>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { CustomFieldService } from '@/core/services'

const ENTITY_LABELS = {
  COLABORADOR: 'este colaborador', CONTRATO: 'este contrato', EMPRESA: 'esta empresa',
  CARGO: 'este cargo', AREA: 'esta área', SEDE: 'esta sede',
}

// CDEF010 regla 5: los campos personalizados aparecen solos en el formulario de su entidad, ordenados por
// display_order, sin tocar el frontend. Este componente lee las definiciones y valores del backend y pinta el
// control según el tipo de cada campo.
export default {
  name: 'CustomFieldsSection',
  props: {
    entity: { type: String, required: true },
    entityId: { type: [Number, String], required: true },
  },
  data() {
    return { loading: true, saving: false, items: [], values: {} }
  },
  computed: {
    entityLabel() { return ENTITY_LABELS[this.entity] || 'esta entidad' },
    // Agrupa por empresa: los campos sin empresa aplican a todas.
    groups() {
      const map = {}
      this.items.forEach(it => {
        const key = it.field.companyId ? String(it.field.companyId) : 'ALL'
        if (!map[key]) map[key] = { key, label: it.field.companyName || 'Todas las empresas', items: [] }
        map[key].items.push(it)
      })
      return Object.values(map).sort((a, b) => (a.key === 'ALL' ? -1 : b.key === 'ALL' ? 1 : a.label.localeCompare(b.label)))
    },
  },
  watch: {
    entityId() { this.load() },
  },
  methods: {
    async load() {
      if (!this.entityId) return
      this.loading = true
      try {
        const resp = await CustomFieldService.getValues(this.entity, this.entityId)
        this.items = resp.payload || []
        const values = {}
        this.items.forEach(it => { values[it.field.id] = it.value != null ? it.value : null })
        this.values = values
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los campos personalizados')
      } finally {
        this.loading = false
      }
    },
    async save() {
      this.saving = true
      try {
        const list = this.items.map(it => ({
          customFieldId: it.field.id,
          value: this.values[it.field.id] != null ? String(this.values[it.field.id]) : '',
        }))
        const resp = await CustomFieldService.saveValues(this.entity, this.entityId, list)
        this.items = resp.payload || []
        Alert.success('Campos personalizados guardados')
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'No se pudieron guardar los campos personalizados')
      } finally {
        this.saving = false
      }
    },
  },
  created() { this.load() },
}
</script>

<style lang="stylus">
.cf-section
  padding 4px 0
</style>
